// Pure date/time + layout helpers for the Scheduler module (ported from the
// approved prototype). Times are minutes-from-midnight; dates are JS Date.

import { decrypt } from "../../../utils";

// Staff/client photos are AES-encrypted (CryptoJS "U2FsdGVk" prefix); decrypt
// only when the value is ciphertext so plain S3 URLs pass through untouched.
export const photoOf = (v) => (typeof v === "string" && v.startsWith("U2FsdGVk") ? decrypt(v) : v || null);

export const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
export const MON_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};
export const addDays = (d, n) => {
  const x = startOfDay(d);
  x.setDate(x.getDate() + n);
  return x;
};
export const startOfWeek = (d) => addDays(d, -startOfDay(d).getDay());
export const sameDay = (a, b) => startOfDay(a).getTime() === startOfDay(b).getTime();

const _hm = (m) => {
  const mm = (((m % 1440) + 1440) % 1440);
  const h = Math.floor(mm / 60);
  const min = mm % 60;
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(min).padStart(2, "0")}`;
};
const _mer = (m) => (Math.floor((((m % 1440) + 1440) % 1440) / 60) < 12 ? "AM" : "PM");

export const fmtTime = (m) => `${_hm(m)} ${_mer(m)}`;
// Compact time for month chips: "7a", "3p", "7:30a".
export const fmtTimeShort = (mins) => {
  const mm = (((mins % 1440) + 1440) % 1440);
  let h = Math.floor(mm / 60);
  const m = mm % 60;
  const ap = h >= 12 ? "p" : "a";
  h = h % 12 || 12;
  return m === 0 ? `${h}${ap}` : `${h}:${String(m).padStart(2, "0")}${ap}`;
};
export const fmtRange = (a, b) =>
  _mer(a) === _mer(b) ? `${_hm(a)} – ${_hm(b)} ${_mer(b)}` : `${_hm(a)} ${_mer(a)} – ${_hm(b)} ${_mer(b)}`;

export const hrsBetween = (a, b) => Math.max(0, (b - a) / 60);

export const weekLabel = (weekStart) => {
  const a = weekStart;
  const b = addDays(a, 6);
  return a.getMonth() === b.getMonth()
    ? `${MON_SHORT[a.getMonth()]} ${a.getDate()} – ${b.getDate()}`
    : `${MON_SHORT[a.getMonth()]} ${a.getDate()} – ${MON_SHORT[b.getMonth()]} ${b.getDate()}`;
};

// ISO date (YYYY-MM-DD) for API params/filtering.
export const isoDate = (d) => {
  const x = startOfDay(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
};

// Google-Calendar style overlap clustering -> assigns each item a column.
export const clustersOf = (list) => {
  const res = [];
  let cluster = [];
  let clusterEnd = -1;
  const flush = () => {
    const colEnds = [];
    cluster.forEach((ev) => {
      let placed = false;
      for (let c = 0; c < colEnds.length; c++) {
        if (ev.startMin >= colEnds[c]) {
          ev.col = c;
          colEnds[c] = ev.endMin;
          placed = true;
          break;
        }
      }
      if (!placed) {
        ev.col = colEnds.length;
        colEnds.push(ev.endMin);
      }
    });
    const ncol = colEnds.length;
    res.push({ ncol, items: cluster.map((e) => ({ s: e, col: e.col })) });
    cluster = [];
  };
  [...list]
    .sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin)
    .forEach((s) => {
      if (cluster.length && s.startMin >= clusterEnd) {
        flush();
        clusterEnd = -1;
      }
      cluster.push(s);
      clusterEnd = Math.max(clusterEnd, s.endMin);
    });
  if (cluster.length) flush();
  return res;
};

export const initials = (name) =>
  (name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0] || "")
    .join("")
    .toUpperCase() || "?";

// Category + display metadata for a shift, matching the prototype's pills/cards.
export const entryMeta = (s) => {
  const appt = s.kind === "appointment";
  const who = s.staff?.name || "Unassigned";
  const staffAv = s.staff?.name ? initials(s.staff.name) : null;
  const staffPhoto = photoOf(s.staff?.photo);
  if (s.status === "open") {
    return { cat: "open", pill: "Open", title: s.open?.role ? `${s.open.role} needed` : "Open shift", staffName: null, staffAv: null, staffPhoto: null, facility: s.facility?.name || "", clientName: s.type === "facility" ? "" : s.clientName || s.client?.name || "" };
  }
  if (s.type === "facility") {
    return {
      cat: "facility",
      appt,
      pill: appt ? "Facility Appointment" : "Facility Schedule",
      title: appt ? s.apptTitle || "Appointment" : who,
      staffName: s.staff?.name || null,
      staffAv,
      staffPhoto,
      facility: s.facility?.name || "",
      clientName: "",
    };
  }
  return {
    cat: "client",
    appt,
    pill: appt ? "Client Appointment" : "Client Visit",
    title: appt ? s.apptTitle || "Appointment" : who,
    staffName: s.staff?.name || null,
    staffAv,
    staffPhoto,
    facility: s.facility?.name || "",
    clientName: s.clientName || s.client?.name || "",
  };
};

export const STATUS_BADGE = {
  scheduled: { color: "blue", label: "Scheduled" },
  inprogress: { color: "green", label: "In Progress" },
  completed: { color: "gray", label: "Completed" },
  exception: { color: "red", label: "Exception" },
  open: { color: "yellow", label: "Open" },
  absent: { color: "gray", label: "Absent" },
};

// Prototype palette: client = violet, facility = teal, open = accent.
export const CAT_COLOR = {
  client: { soft: "#F5F3FF", ink: "#6D28D9", line: "#7C3AED" },
  facility: { soft: "#F0FDFA", ink: "#0F766E", line: "#0D9488" },
  open: { soft: "#FFF8EB", ink: "#B45309", line: "#FFBA45" },
};
